/**
 * Evidence photo upload — the client half of `POST /files`.
 *
 * WHY NOT THE SHARED API CLIENT: `@sonauto/api-client` JSON-stringifies every
 * body, and an upload needs (a) raw binary bytes and (b) UPLOAD progress, which
 * `fetch` cannot report. So this module speaks XMLHttpRequest directly, but
 * reuses the SAME stored tokens — and on a 401 it triggers the shared client's
 * single-flight refresh (via a cheap authenticated GET) instead of implementing
 * a second refresh path that could race the first.
 *
 * CLIENT-SIDE COMPRESSION: the server caps uploads at 5 MB and modern phone
 * cameras produce 8–15 MB HEIC/JPEG originals. Re-encoding through a canvas to
 * JPEG at ≤1600 px longest edge lands a typical evidence photo at 200–600 kB —
 * comfortably under the cap, uploads in seconds on restaurant wifi, and is
 * still plenty of resolution for "was the table actually cleaned".
 */
import { api, API_BASE_URL } from './api';
import { deviceId, readTokens } from './session-store';
import type { FileUploadResponse } from './contracts';

/** Mirrors MAX_UPLOAD_BYTES in the API's services/file-type.ts. */
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/** Longest edge after re-encode. See the header comment. */
const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.85;
/** Second-chance quality if the first encode somehow exceeds the cap. */
const JPEG_QUALITY_FALLBACK = 0.6;

export type UploadPurpose = 'EVIDENCE' | 'ATTACHMENT';

export class UploadError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'UploadError';
    this.status = status;
  }
}

// ---------------------------------------------------------------------------
// Compression
// ---------------------------------------------------------------------------

function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Không đọc được ảnh vừa chụp.'));
    };
    image.src = url;
  });
}

function canvasToJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob === null) reject(new Error('Không nén được ảnh.'));
        else resolve(blob);
      },
      'image/jpeg',
      quality,
    );
  });
}

/**
 * Re-encode a captured photo to JPEG, longest edge ≤ 1600 px.
 *
 * Always re-encodes (even small files) so an iPhone HEIC — which the server's
 * type sniffer rejects — leaves this function as a plain JPEG the server
 * accepts.
 */
export async function compressImage(file: Blob): Promise<Blob> {
  const image = await loadImage(file);
  const scale = Math.min(1, MAX_DIMENSION / Math.max(image.naturalWidth, image.naturalHeight));
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (context === null) throw new Error('Trình duyệt không hỗ trợ xử lý ảnh.');
  context.drawImage(image, 0, 0, width, height);

  let blob = await canvasToJpeg(canvas, JPEG_QUALITY);
  if (blob.size > MAX_UPLOAD_BYTES) {
    blob = await canvasToJpeg(canvas, JPEG_QUALITY_FALLBACK);
  }
  if (blob.size > MAX_UPLOAD_BYTES) {
    throw new Error('Ảnh sau khi nén vẫn vượt 5 MB. Hãy chụp lại ở khoảng cách xa hơn.');
  }
  return blob;
}

// ---------------------------------------------------------------------------
// Upload (XHR for progress)
// ---------------------------------------------------------------------------

function xhrUpload(
  blob: Blob,
  purpose: UploadPurpose,
  fileName: string,
  accessToken: string,
  onProgress: (fraction: number) => void,
): Promise<FileUploadResponse> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const params = new URLSearchParams({ purpose, fileName });
    xhr.open('POST', `${API_BASE_URL}/v1/files?${params.toString()}`);
    xhr.responseType = 'json';
    xhr.timeout = 60_000;
    xhr.setRequestHeader('authorization', `Bearer ${accessToken}`);
    // The server sniffs the real type anyway; this is the declared one.
    xhr.setRequestHeader('content-type', blob.type === '' ? 'image/jpeg' : blob.type);
    xhr.setRequestHeader('x-file-name', fileName);
    const device = deviceId();
    if (device !== null) xhr.setRequestHeader('x-device-id', device);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
    };
    xhr.onerror = () => reject(new UploadError('Mất kết nối khi đang tải ảnh lên.', 0));
    xhr.ontimeout = () => reject(new UploadError('Tải ảnh lên quá lâu, đã huỷ.', 0));
    xhr.onload = () => {
      const body: unknown = xhr.response;
      if (xhr.status === 201 && body !== null && typeof body === 'object' && 'fileId' in body) {
        resolve(body as FileUploadResponse);
        return;
      }
      const message =
        body !== null &&
        typeof body === 'object' &&
        'error' in body &&
        typeof (body as { error?: { message?: unknown } }).error?.message === 'string'
          ? ((body as { error: { message: string } }).error.message)
          : `Máy chủ từ chối ảnh (HTTP ${xhr.status}).`;
      reject(new UploadError(message, xhr.status));
    };
    xhr.send(blob);
  });
}

/**
 * Upload a (compressed) blob. On a 401 — the access token expired mid-shift —
 * `api.me()` is called purely to drive the shared client's single-flight
 * refresh, and the upload is retried once with the rotated token.
 */
export async function uploadFile(
  blob: Blob,
  purpose: UploadPurpose,
  fileName: string,
  onProgress: (fraction: number) => void,
): Promise<FileUploadResponse> {
  const tokens = readTokens();
  if (tokens === null) throw new UploadError('Phiên đăng nhập đã hết. Hãy đăng nhập lại.', 401);
  try {
    return await xhrUpload(blob, purpose, fileName, tokens.accessToken, onProgress);
  } catch (error) {
    if (error instanceof UploadError && error.status === 401) {
      await api.me(); // side effect: single-flight token refresh + persist
      const refreshed = readTokens();
      if (refreshed === null) throw error;
      return xhrUpload(blob, purpose, fileName, refreshed.accessToken, onProgress);
    }
    throw error;
  }
}

/**
 * Fetch a stored file back as an object URL — the server serves files behind
 * the session (an `<img src>` cannot carry the Bearer header), so previews go
 * through fetch + blob. Caller owns the URL and must revoke it.
 */
export async function fetchFileObjectUrl(fileId: string): Promise<string> {
  const tokens = readTokens();
  if (tokens === null) throw new UploadError('Phiên đăng nhập đã hết.', 401);
  const response = await fetch(`${API_BASE_URL}/v1/files/${encodeURIComponent(fileId)}`, {
    headers: { authorization: `Bearer ${tokens.accessToken}` },
  });
  if (!response.ok) throw new UploadError(`Không tải được tệp (HTTP ${response.status}).`, response.status);
  return URL.createObjectURL(await response.blob());
}
